import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Container from "@mui/material/Container";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import SectionHeading from "components/allInOneDashboard/SectionHeading";
import LockedProductCard from "components/allInOneDashboard/LockedProductCard";
import UnlockedProductCard from "components/allInOneDashboard/UnlockedProductCard";
import MobileEssentialCard from "components/allInOneDashboard/MobileEssentialCard";
import PortfolioHealthCheckBanner from "components/allInOneDashboard/PortfolioHealthCheckBanner";
import DashboardAdBanner from "components/allInOneDashboard/DashboardAdBanner";
import PerformanceWidget from "components/allInOneDashboard/PerformanceWidget";
import PrivateWealthSection from "components/allInOneDashboard/PrivateWealthSection";
import FeaturedBlogCard from "components/allInOneDashboard/FeaturedBlogCard";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import MarketPulseCard from "components/allInOneDashboard/MarketPulse";
import EssentialCardSkeleton from "components/allInOneDashboard/EssentialCardSkeleton";
import {
  hasProductPendingCall,
  getProductPendingCount,
} from "components/allInOneDashboard/utils/pendingCall";
import { usePromoAd } from "hooks/usePromoAd";
import { useDashboardOffer } from "hooks/useDashboardOffer";
import { useDashboardIconImages } from "hooks/useDashboardIconImages";
import { useFetch } from "hooks/useFetch";
import { localStore } from "stores/localStorage";
import { axios } from "helper/axios";
import { getAutoTitleColor } from "helper/imageTitleColor";
import { thirdpartyConstants } from "stores/thirdparty/thirdpartyConstants";
import { config } from "config";
import { jarvisOnestockOnboardingApis } from "stores/jarvisOnestock/jarvisOnestockOnboarding/jarvisOnestockOnboardingApis";
import { jarvisOnestockOnboardingParser } from "stores/jarvisOnestock/jarvisOnestockOnboarding/jarvisOnestockOnboardingParser";
import { onboardingApis } from "stores/onboarding/onboardingApis";
import { onboardingParsers } from "stores/onboarding/onboardingParsers";
import { jarvisRMSOnboardingApis } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingApis";
import { jarvisRMSOnboardingParser } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingParser";
import { useLocation } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  BRAND_BLUE,
  MOBILE_GUTTER,
  MOBILE_SECTION_GAP,
} from "components/allInOneDashboard/constants/mobileTheme";

const isPhcProduct = (product) =>
  product?.code === config.productCodes.PHC ||
  product?.id === "phc" ||
  product?.code === "PHC";

// Festive offer skin: the background image (`essentialsBgWeb` /
// `essentialsBgMobile`), heading colour and subtitle all come from
// `dashboard/offers`. Images are made to the full-size spec (web 1440x1360,
// mobile 375x313 at 1x) and simply cover the section. Without an image URL,
// or if it fails to load, the normal card is shown.
const ESSENTIALS_OFFER_TITLE_COLOR = "#FFFFFF";
// Desktop festive heading (Figma 266:36102): no icon, 28px title + subtitle.
const ESSENTIALS_OFFER_TITLE_SX = {
  fontSize: { xs: 18, md: "clamp(1.375rem, 1rem + 0.9vw, 1.75rem)" },
  letterSpacing: "-0.03em",
  lineHeight: 1,
};
const ESSENTIALS_OFFER_SUBTITLE_SX = {
  fontSize: { xs: 14, md: "clamp(0.9375rem, 0.75rem + 0.55vw, 1.16rem)" },
  fontWeight: 400,
  letterSpacing: "-0.03em",
  lineHeight: 1,
  // Follows the title colour, so it suits both dark and light images.
  opacity: 0.8,
};
const ESSENTIALS_DEFAULT_ICON =
  "/images/product-info/investing-essentials-icon.png";

const essentialsCarouselSx = {
  display: { xs: "flex", md: "grid" },
  gridTemplateColumns: {
    md: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: { xs: 1.5, md: 2.5 },
  overflowX: { xs: "auto", md: "visible" },
  scrollSnapType: { xs: "x mandatory", md: "none" },
  WebkitOverflowScrolling: "touch",
  alignItems: "stretch",
  pb: 1,
  mx: { xs: -MOBILE_GUTTER, md: -3 },
  px: { xs: MOBILE_GUTTER, md: 3 },
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

const UnifiedDashboardContainer = ({
  blogs,
  investingEssentials,
  privateWealth,
  disabledProductCodes,
  productsLoading,
  pendingCallData,
  getPortfolioOnboardingDataAction,
  updatePortfolioOnboardingDataAction,
  updateOnestockOnboardingDataAction,
  updateRMSOnboardingDataAction,
  getRiskDetails,
  getFirstRender,
  getBlogsAction,
  getProductDetailsAction,
  getPendingCallAction,
}) => {
  const history = useHistory();
  const { ads: promoAds } = usePromoAd();
  const { offer, isActive: isOfferActive } = useDashboardOffer();
  const { iconByProductCode } = useDashboardIconImages();
  const {
    data: marketPulseResponse,
    loading: marketPulseLoading,
    error: marketPulseError,
    refetch: refetchMarketPulse,
  } = useFetch({ type: "current" });
  const [isMarketPulseExpanded, setIsMarketPulseExpanded] = useState(false);
  // Backend background URLs by load state. The festive look switches on only
  // once its image has loaded (stored with its width/height ratio, which sizes
  // the mobile section); a failed image keeps the normal card.
  const [loadedBgAspects, setLoadedBgAspects] = useState({});
  const [failedBgUrls, setFailedBgUrls] = useState([]);
  // Backgrounds are first requested with CORS so their pixels can be read to
  // pick the title colour. A URL lands here when its server sends no CORS
  // headers; it is then reloaded normally and the title keeps the default.
  const [noCorsBgUrls, setNoCorsBgUrls] = useState([]);
  // Keyed by layout + URL: the heading sits in a different part of the
  // mobile and desktop images, even when both use the same URL.
  const [autoTitleColors, setAutoTitleColors] = useState({});
  // The mobile and desktop sections are both mounted and toggled with CSS at
  // `md`; only the visible one may load its festive image, so phones don't
  // download the large web image and desktops don't download the mobile one.
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up("md"), {
    noSsr: true,
  });

  const location = useLocation();

  // Single fetch on dashboard visit. Skip if product-details is already
  // in flight (guards against accidental remounts).
  useEffect(() => {
    if (location.pathname !== "/customer/dashboard") return;
    if (productsLoading) return;

    getProductDetailsAction?.();
    getPortfolioOnboardingDataAction?.();
    getPendingCallAction?.();
    if (!blogs?.length) {
      getBlogsAction?.();
    }
    // Action creators from connect/bindActionCreators are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const COLLAPSE_MS = 480;

  const getRiskDetailsForProduct = useCallback(
    async (productCode) => {
      try {
        const response = await axios.get(
          `${thirdpartyConstants.PORTFOLIO_SUBSCRIPTION_PLANS_API}?productCode=${productCode}`,
        );
        if (response?.[0]?.riskData) {
          getRiskDetails(response[0].riskData);
        }
        return response;
      } catch (error) {
        console.error("API Error:", error);
      }
    },
    [getRiskDetails],
  );

  // Refresh product onboarding flags before navigating so stale persisted
  // isOnboardingCompleted: 1 does not bounce locked users back to dashboard.
  const syncLockedProductOnboardingStatus = useCallback(
    async (product) => {
      const code = product.code;
      try {
        if (code === config.productCodes.ONESTOCK) {
          const res = await jarvisOnestockOnboardingApis.getOnboardingData();
          const parsed = jarvisOnestockOnboardingParser.onboardingData(res);
          updateOnestockOnboardingDataAction?.({
            currentStage: parsed.currentStage,
            isOnboardingCompleted: parsed.isOnboardingCompleted ?? 0,
            isPaymentCompleted: parsed.isPaymentCompleted ?? 0,
          });
          return;
        }
        if (code === config.productCodes.PORTFOLIO) {
          const res = await onboardingApis.getOnboardingData();
          const parsed = onboardingParsers.onboardingData(res);
          updatePortfolioOnboardingDataAction?.({
            currentStage: parsed.currentStage,
            isOnboardingCompleted: parsed.isOnboardingCompleted ?? 0,
            isPaymentCompleted: parsed.isPaymentCompleted ?? 0,
          });
          return;
        }
        if (code === config.productCodes.RMS) {
          const res = await jarvisRMSOnboardingApis.getOnboardingData();
          const parsed = jarvisRMSOnboardingParser.onboardingData(res);
          updateRMSOnboardingDataAction?.({
            currentStage: parsed.currentStage,
            isOnboardingCompleted: parsed.isOnboardingCompleted ?? 0,
            isPaymentCompleted: parsed.isPaymentCompleted,
          });
        }
      } catch (error) {
        console.error("Onboarding status sync failed:", error);
      }
    },
    [
      updateOnestockOnboardingDataAction,
      updatePortfolioOnboardingDataAction,
      updateRMSOnboardingDataAction,
    ],
  );

  const handleProductClick = useCallback(
    async (product) => {
      if (!product.onboardingPath) {
        if (product.productInfoPath) {
          if (product.code) {
            localStore.setActiveProduct(product.code);
          }
          history.push(product.productInfoPath);
        }
        return;
      }

      localStore.setActiveProduct(product.code);

      if (!product.isLocked) {
        history.push(product.dashboardPath);
        return;
      }

      // Locked product with a product-info page (e.g. Prime Know More)
      if (product.productInfoPath) {
        history.push(product.productInfoPath);
        return;
      }

      if (product.clearPlanIdOnStart) {
        updatePortfolioOnboardingDataAction({ planId: null });
      }

      await syncLockedProductOnboardingStatus(product);
      await getRiskDetailsForProduct(product.code);

      if (product.needsFirstRender) {
        getFirstRender(true);
      }

      history.push(product.onboardingPath);
    },
    [
      history,
      updatePortfolioOnboardingDataAction,
      syncLockedProductOnboardingStatus,
      getRiskDetailsForProduct,
      getFirstRender,
    ],
  );

  const handleAdClick = useCallback(
    (ad) => {
      if (!ad?.redirectUrl) {
        return;
      }

      if (ad.redirectType === "external") {
        window.open(
          ad.redirectUrl,
          ad.openInNewTab ? "_blank" : "_self",
          "noopener,noreferrer",
        );
        return;
      }

      history.push(ad.redirectUrl);
    },
    [history],
  );

  const handleBlogReadMore = useCallback(() => {
    const blog = blogs?.[0];
    if (blog?.readMoreLink) {
      window.open(blog.readMoreLink, "_blank", "noopener,noreferrer");
      return;
    }
    history.push("/knowledge-centre/blogs");
  }, [blogs, history]);

  const renderEssentialProduct = (product, { compact = false } = {}) => {
    if (compact) {
      return (
        <MobileEssentialCard
          key={product.code || product.id}
          product={product}
          onClick={() => handleProductClick(product)}
        />
      );
    }

    const card = product.isLocked ? (
      <LockedProductCard
        product={product}
        onClick={() => handleProductClick(product)}
      />
    ) : (
      <UnlockedProductCard
        product={product}
        onClick={() => handleProductClick(product)}
        hasPendingCall={hasProductPendingCall(pendingCallData, product)}
        pendingCount={getProductPendingCount(pendingCallData, product.code)}
      />
    );

    return (
      <Box
        key={product.code || product.id}
        sx={{
          flex: { xs: "0 0 auto", md: "unset" },
          width: {
            xs: "min(78vw, 280px)",
            sm: "clamp(240px, 44vw, 286px)",
            md: "auto",
          },
          minWidth: { md: 0 },
          scrollSnapAlign: { xs: "start", md: "unset" },
          minHeight: { xs: 280, md: 300 },
          height: "100%",
          display: "flex",
          "& > *": {
            flex: 1,
            width: "100%",
            minHeight: "100%",
          },
        }}
      >
        {card}
      </Box>
    );
  };

  const featuredBlog = blogs?.[0];
  // Only show skeletons on the true first load, never on background refetches
  // (e.g. after onboarding), so already-rendered cards don't flicker away.
  const showEssentialsSkeleton =
    productsLoading && investingEssentials.length === 0;

  const essentialsWithIcons = useMemo(
    () =>
      investingEssentials.map((product) => {
        if (isPhcProduct(product)) {
          return product;
        }
        const remoteUrl = iconByProductCode[product.code];
        if (!remoteUrl) {
          return product;
        }
        return { ...product, icon: remoteUrl, localIcon: product.icon };
      }),
    [investingEssentials, iconByProductCode],
  );

  const phcProduct = essentialsWithIcons.find(isPhcProduct);
  const mobileEssentials = essentialsWithIcons.filter((p) => !isPhcProduct(p));

  const handlePhcBannerClick = () => {
    if (phcProduct) {
      handleProductClick(phcProduct);
      return;
    }
    history.push("/customer/product-info/phc");
  };

  const essentialsSection = ({ compact = false, overlapMarketPulse = false } = {}) => {
    const bgUrl = compact ? offer.essentialsBgMobile : offer.essentialsBgWeb;
    const isLayoutVisible = compact ? !isDesktopLayout : isDesktopLayout;
    const activeBg =
      isOfferActive && isLayoutVisible && bgUrl && !failedBgUrls.includes(bgUrl)
        ? bgUrl
        : null;
    const autoColorKey = `${compact ? "mobile" : "desktop"}|${activeBg}`;
    const bgAspect = activeBg ? loadedBgAspects[activeBg] : null;
    const showIllustratedBg = Boolean(bgAspect);
    const isFestiveDesktop = showIllustratedBg && !compact;
    // Mobile festive layout: the section takes the image's shape, the heading
    // sits in the image's top strip and the cards are pinned to the bottom,
    // leaving the band between them for the offer art.
    const isFestiveMobile = showIllustratedBg && compact;
    // An explicit colour from the API wins; otherwise the colour measured from
    // the image (white on dark/saturated art, dark on light art); otherwise white.
    const titleColor = showIllustratedBg
      ? offer.essentialsTitleColor ||
        autoTitleColors[autoColorKey] ||
        ESSENTIALS_OFFER_TITLE_COLOR
      : undefined;
    const bgTriedCors = activeBg ? !noCorsBgUrls.includes(activeBg) : false;

    return (
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          overflow: "hidden",
          borderRadius: { xs: "0 0 24px 24px", md: "24px" },
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 0 9px rgba(0,0,0,0.11)",
          p: { xs: MOBILE_GUTTER, md: 3 },
          pt: isFestiveMobile
            ? { xs: 1.5 }
            : overlapMarketPulse
              ? isMarketPulseExpanded
                ? { xs: 2, md: 3 }
                : { xs: 5.5, md: 6 }
              : { xs: 3, md: 3 },
          ...(overlapMarketPulse
            ? {
              "&&": {
                mt: "calc(-10px - 30px) !important",
              },
              transition: `margin-top ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1), padding-top ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            }
            : { mt: { xs: 0, md: 0 } }),
          ...(isFestiveMobile
            ? {
              pb: { xs: 2 },
              aspectRatio: String(bgAspect),
              display: "flex",
              flexDirection: "column",
            }
            : null),
          background:
            "radial-gradient(ellipse at top left, rgba(148,187,246,0.15), transparent 60%)",
        }}
      >
        {activeBg ? (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
              overflow: "hidden",
              opacity: showIllustratedBg ? 1 : 0,
              transition: "opacity 300ms ease",
            }}
          >
            <Box
              component="img"
              // Keep crossOrigin before src so the first request is already
              // a CORS one.
              crossOrigin={bgTriedCors ? "anonymous" : undefined}
              src={activeBg}
              alt=""
              // Mobile and desktop sections are both mounted (CSS toggles
              // them), so lazy-load to skip the hidden layout's image.
              loading="lazy"
              onLoad={(event) => {
                const img = event.currentTarget;
                const { naturalWidth, naturalHeight } = img;
                const aspect =
                  naturalWidth && naturalHeight
                    ? naturalWidth / naturalHeight
                    : 1;
                setLoadedBgAspects((aspects) =>
                  aspects[activeBg] ? aspects : { ...aspects, [activeBg]: aspect },
                );
                const autoColor = bgTriedCors
                  ? getAutoTitleColor(img, { compact })
                  : null;
                if (autoColor) {
                  setAutoTitleColors((colors) =>
                    colors[autoColorKey] === autoColor
                      ? colors
                      : { ...colors, [autoColorKey]: autoColor },
                  );
                }
              }}
              onError={() => {
                // A failed CORS attempt is retried as a plain image load
                // (removing crossOrigin makes the browser fetch it again);
                // only a failed plain load drops back to the normal card.
                if (bgTriedCors) {
                  setNoCorsBgUrls((urls) =>
                    urls.includes(activeBg) ? urls : [...urls, activeBg],
                  );
                  return;
                }
                setFailedBgUrls((urls) =>
                  urls.includes(activeBg) ? urls : [...urls, activeBg],
                );
              }}
              sx={
                compact
                  ? {
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "top center",
                  }
                  : {
                    // Desktop: full width at the image's own shape (never
                    // stretched), pinned to the top. A banner-shaped image
                    // is shorter than the 2x2 tablet section, so its bottom
                    // edge fades into the filler below; a tall image is just
                    // cropped by the section.
                    position: "absolute",
                    top: 0,
                    left: 0,
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maskImage:
                      "linear-gradient(to bottom, #000 calc(100% - 72px), transparent)",
                    WebkitMaskImage:
                      "linear-gradient(to bottom, #000 calc(100% - 72px), transparent)",
                  }
              }
            />
            {/* Desktop filler under the image: the same image stretched and
                heavily blurred, so the rest of the section keeps the offer's
                colours without showing distorted art. It is an <img> in the
                same CORS mode as the main one so both share one download. */}
            {!compact && showIllustratedBg ? (
              <Box
                component="img"
                crossOrigin={bgTriedCors ? "anonymous" : undefined}
                src={activeBg}
                alt=""
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: -1,
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  filter: "blur(32px)",
                  transform: "scale(1.15)",
                }}
              />
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            ...(isFestiveMobile
              ? {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }
              : null),
          }}
        >
          <SectionHeading
            title="Investing Essentials"
            iconSrc={ESSENTIALS_DEFAULT_ICON}
            titleColor={titleColor}
            titleSx={isFestiveDesktop ? ESSENTIALS_OFFER_TITLE_SX : null}
            subtitle={isFestiveDesktop ? offer.essentialsSubtitle : null}
            subtitleSx={{ ...ESSENTIALS_OFFER_SUBTITLE_SX, color: titleColor }}
            sx={showIllustratedBg ? { pr: { md: "240px" } } : undefined}
          />
          <Box sx={essentialsCarouselSx}>
            {showEssentialsSkeleton
              ? Array.from({ length: 4 }).map((_, index) => (
                <EssentialCardSkeleton
                  key={`essential-skeleton-${index}`}
                  compact={compact}
                />
              ))
              : (compact ? mobileEssentials : essentialsWithIcons).map(
                (product) => renderEssentialProduct(product, { compact }),
              )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ px: { xs: 0, md: 4, lg: 7.5 }, position: "relative" }}
    >
      {/* Mobile layout — Figma Main Dashboard */}
      <Stack
        spacing={MOBILE_SECTION_GAP}
        sx={{ display: { xs: "flex", md: "none" }, px: 0, pt: 1 }}
      >
        {essentialsSection({ compact: true, overlapMarketPulse: false })}

        <Box sx={{ px: MOBILE_GUTTER }}>
          <PortfolioHealthCheckBanner onClick={handlePhcBannerClick} />
        </Box>

        {/* <Box sx={{ px: MOBILE_GUTTER }}>
          <PerformanceWidget />
        </Box> */}

        <Box sx={{ px: MOBILE_GUTTER }}>
          <DashboardAdBanner ads={promoAds} onClick={handleAdClick} />
        </Box>

        <Box sx={{ px: MOBILE_GUTTER }}>
          <PrivateWealthSection
            products={privateWealth}
            disabledProductCodes={disabledProductCodes}
            onProductClick={handleProductClick}
            loading={productsLoading}
            pendingCallData={pendingCallData}
          />
        </Box>

        <Box sx={{ px: MOBILE_GUTTER }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Text
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: "#111827",
                letterSpacing: "-0.02em",
              }}
            >
              Blogs
            </Text>
            <Box
              component="button"
              type="button"
              onClick={() => history.push("/knowledge-centre/blogs")}
              sx={{
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0,
                font: "inherit",
                color: BRAND_BLUE,
                fontSize: 14,
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              See all
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </Box>
          </Stack>
          <FeaturedBlogCard
            blog={featuredBlog}
            onReadMore={handleBlogReadMore}
          />
        </Box>

        <AllInOneFooter />
      </Stack>

      {/* Desktop / tablet layout */}
      <Stack spacing={3} sx={{ display: { xs: "none", md: "flex" } }}>
        <Box
          sx={{
            position: "relative",
            zIndex: 5,
            mt: isMarketPulseExpanded ? -2.5 : -5,
            transition: `margin-top ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          <MarketPulseCard
            response={marketPulseResponse}
            loading={marketPulseLoading}
            error={marketPulseError}
            onRetry={refetchMarketPulse}
            onExpandedChange={setIsMarketPulseExpanded}
          />
        </Box>

        {essentialsSection({ compact: false, overlapMarketPulse: true })}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <DashboardAdBanner ads={promoAds} onClick={handleAdClick} />
          {/* <PerformanceWidget /> */}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems="stretch"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Box
            sx={{
              flex: "1 1 0%",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <PrivateWealthSection
              products={privateWealth}
              disabledProductCodes={disabledProductCodes}
              onProductClick={handleProductClick}
              loading={productsLoading}
              pendingCallData={pendingCallData}
            />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: 319 },
              flex: { md: "0 0 319px" },
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <FeaturedBlogCard
              blog={featuredBlog}
              onReadMore={handleBlogReadMore}
            />
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};

export default UnifiedDashboardContainer;
